import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { LANE_BOTTOM } from "@/components/chat/SidebarChat/chatWidgetLayout";
import { CORNER_Z, cornerRightPosition, cornerWidth } from "./bottomRightStack";

/**
 * The bottom-right corner is shared by several transient surfaces — the chat
 * nudge bubble, the upload/processing card and the toaster. Each used to
 * position itself `fixed` at the same offset, so any two on screen at once
 * landed on top of each other (the "multiple overlapping popups" report).
 *
 * This module is the single owner of that corner. Everything except the toaster
 * renders into ONE flex column, so surfaces stack as siblings and cannot
 * overlap by construction; the toaster is pushed above the column by the height
 * the lane publishes as `--bottom-right-lane` (see `components/ui/sonner.tsx`),
 * because sonner renders its own fixed viewport and cannot live inside it.
 *
 * Lane geometry, measured from the viewport bottom:
 *   below LANE_BOTTOM   the collapsed chat pill (BOTTOM_MARGIN + COLLAPSED_HEIGHT)
 *   LANE_BOTTOM         this lane, growing upwards
 *   above it            the toaster
 *
 * Geometry keeps the lane clear of the COLLAPSED pill. When chat opens, its
 * panel wins the z-stack and the toaster moves beside it; `bottomRightStack.ts`
 * holds that contract for all four surfaces.
 */
/** Gap between the lane's topmost card and the toaster above it. */
const LANE_TOAST_GAP = 8;
const LANE_HEIGHT_VAR = "--bottom-right-lane";

/**
 * Stacking order inside the lane, top-down. Explicit so the result never
 * depends on which surface happened to mount first.
 */
const LANE_ORDER = {
  /** Org-wide billing lockout. Topmost: it explains why the surfaces below it
   *  (and every mutating control on the page) are refusing to do anything. */
  billingRestriction: 0,
  uploadQueue: 1,
  storageWarning: 1.5,
  /** A generated protocol waiting to be filed into a project. Below the upload
   *  card, which reports a batch already in flight. */
  protocolSave: 2,
  /** Closest to the pill it belongs to. */
  chatNudge: 3,
} as const;

export type LaneSlotName = keyof typeof LANE_ORDER;

let laneElement: HTMLDivElement | null = null;

/** The lane node, created on first use and kept for the page's lifetime. */
function getLane(): HTMLDivElement {
  if (laneElement?.isConnected) return laneElement;

  const el = document.createElement("div");
  el.id = "bottom-right-lane";
  el.dataset.testid = "bottom-right-lane";
  el.className = "pointer-events-none fixed flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2";
  // One corner, one width: the lane is exactly as wide as the chat (round-6).
  el.style.width = cornerWidth;
  el.style.right = cornerRightPosition;
  el.style.bottom = `${LANE_BOTTOM}px`;
  el.style.zIndex = String(CORNER_Z.lane);
  document.body.appendChild(el);

  const publishHeight = () => {
    const height = el.getBoundingClientRect().height;
    document.documentElement.style.setProperty(
      LANE_HEIGHT_VAR,
      height > 0 ? `${Math.round(height) + LANE_TOAST_GAP}px` : "0px"
    );
  };
  publishHeight();
  // ponytail: no observer in jsdom — tests assert stacking, not toast offset.
  if (typeof ResizeObserver !== "undefined") new ResizeObserver(publishHeight).observe(el);

  laneElement = el;
  return el;
}

/**
 * Render `children` as one card in the bottom-right lane. Surfaces must use
 * this instead of anchoring themselves to the corner — that is what made two of
 * them overlap.
 *
 * The slot is full-width so a card that wants the lane's width still gets it,
 * and `[&>*]:ml-auto` pins a NARROWER card (the minimized upload/processing
 * variants, `w-72`) to the lane's right edge instead of leaving it stranded
 * left of it — auto margins are ignored on an auto-width block, so full-width
 * cards are unaffected.
 */
export function BottomRightLaneSlot({
  name,
  children,
}: {
  name: LaneSlotName;
  children: ReactNode;
}) {
  return createPortal(
    <div className="pointer-events-auto w-full [&>*]:ml-auto" style={{ order: LANE_ORDER[name] }}>
      {children}
    </div>,
    getLane()
  );
}
